<!-- eslint-disable max-lines -->
<script lang="ts" setup>
import { useReferentialStore } from '~/stores/referential';
import { useTranslations } from '~/composables/translations';
import { computed, Ref, ref } from 'vue';
import { computeProcessIcon } from '~/composables/icons';

import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import vuAccordion from '@ds/vuekit/src/components/vu-accordion.vue';

import { NlsMaterials } from '~/typings/nls/materials';
import { NlsProcessFamilies } from '~/typings/nls/process_families';

// PROPS
interface Props {
  materials: any;
  isCardView?: boolean;
}

withDefaults(defineProps<Props>(), {
  materials: undefined,
  isCardView: false,
});

// NLS
const { $i18n } = useTranslations('pages/lab/showroom');
const { $i18n: $i18nMachines } = useTranslations('components/showroom/machines');
const { $i18n: $i18nMaterials } = useTranslations('materials');
const { $i18n: $i18nProcessF } = useTranslations('process_families');
const { $i18n: $i18nProducts } = useTranslations('products');

// STORES
const referential = useReferentialStore();

// VARIABLES
const accordionModels = ref(Array(20).fill([]));
const maxReferencesInitialCount = 8;
const maxReferencesIncrementCount = 8;
const emptyText = '-';
const expandedReferences: Ref<Record<string, boolean>> = ref({});
const maxReferencesDisplayedForMaterial: Ref<Record<string, number>> = ref({});

// COMPUTED
const processFamiliesMap = computed(() => referential.mapProcessFamilies);
const materialsMap = computed(() => referential.mapMaterials);
const materialClassesMap = computed(() => referential.mapMaterialClasses);
const allProcessFamiliesIds = computed(() => Object.keys(processFamiliesMap.value));

// METHODS
const canCollapse = (indexMaterialFamily: number) =>
  accordionModels.value[indexMaterialFamily] && accordionModels.value[indexMaterialFamily].length;

const toggleAccordionsOfMaterialFamily = (
  materialFamilyIndex: number,
  materialList: ArrayLike<unknown> | { [s: string]: unknown },
) => {
  accordionModels.value[materialFamilyIndex] = accordionModels.value[materialFamilyIndex].length
    ? []
    : Array.from({ length: Object.values(materialList).length }, (_, i) => i + 1);
};

const getMaxReferencesDisplayedForMaterial = (materialClassId: number) => {
  if (!maxReferencesDisplayedForMaterial.value[materialClassId]) {
    maxReferencesDisplayedForMaterial.value[materialClassId] = maxReferencesInitialCount;
  }

  return maxReferencesDisplayedForMaterial.value[materialClassId];
};

const loadMoreReferences = (materialClassId: number) => {
  const newValue =
    maxReferencesDisplayedForMaterial.value[materialClassId] + maxReferencesIncrementCount;
  maxReferencesDisplayedForMaterial.value[materialClassId] = newValue;
};

const getVisibleMaterials = (materialClassId: number, materialsInClass: object) =>
  Object.values(materialsInClass).slice(0, getMaxReferencesDisplayedForMaterial(materialClassId));

/** Get the number of MM Materials that are not yet displayed */
const getNumReferencesNotDisplayedYet = (
  materials: Record<string, unknown>,
  materialClassId: any,
) => Object.keys(materials).length - getMaxReferencesDisplayedForMaterial(materialClassId);

const getTurnAroundTimeForMaterial = (material: { turnAroundTime: number }) =>
  `${material.turnAroundTime} ${material.turnAroundTime > 1 ? $i18n('daysPlural') : $i18n('day')}`;

const getMaterialPositionId = (
  materialClassIndex: number,
  materialIndex: number,
  materialId: string,
) => [materialClassIndex, materialIndex, materialId].join('-');

const toggleReferenceDetails = (
  materialClassIndex: number,
  materialIndex: number,
  materialId: string,
) => {
  expandedReferences.value[getMaterialPositionId(materialClassIndex, materialIndex, materialId)] =
    !expandedReferences.value[getMaterialPositionId(materialClassIndex, materialIndex, materialId)];
};
</script>
<template>
  <div id="showroom-materials" class="showroom-materials flex flex-column grid-gap-10px">
    <template v-if="!!materials && Object.keys(materials).length">
      <div
        v-for="(materialClassList, materialFamilyId, indexMaterialFamily) in materials"
        :key="indexMaterialFamily"
        class="material-family flex flex-column grid-gap-10px"
      >
        <div class="flex flex-row align-items-center">
          <div class="material-family-name showroom-section-subtitle text--uppercase flex-grow-1">
            {{ materialsMap.family[materialFamilyId] }}
          </div>
          <div class="expand-bloc flex align-items-center">
            <vu-icon
              :class="['chevron', canCollapse(indexMaterialFamily) ? 'expanded' : null]"
              icon="chevron-down"
            />
            <div
              class="expand-all mp3dp-link"
              @click="toggleAccordionsOfMaterialFamily(indexMaterialFamily, materialClassList)"
            >
              {{ canCollapse(indexMaterialFamily) ? $i18n('collapseAll') : $i18n('expandAll') }}
            </div>
          </div>
        </div>
        <div class="showroom-container pa-10px">
          <vu-accordion
            v-model="accordionModels[indexMaterialFamily]"
            animated
            :items="Object.keys(materialClassList).length"
          >
            <template
              v-for="(groupsInClass, materialClassId, indexMaterialClass) in materialClassList"
              #[`title-${indexMaterialClass+1}`]
              :key="indexMaterialClass + 'material-class-id'"
            >
              <div class="title-bar flex flex-wrap align-items-center">
                <div class="material-class-name pa-0">
                  {{
                    $i18nMaterials(
                      materialClassesMap[materialClassId].name as keyof NlsMaterials,
                      {},
                      true,
                    )
                  }}
                </div>
                <div class="flex flex-grow-1">
                  <div class="px-5px separator">-</div>
                  <div class="references-count">
                    {{ Object.keys(groupsInClass).length }}
                    <template v-if="Object.keys(groupsInClass).length > 1">
                      {{ $i18nMachines('references')?.toLowerCase() }}
                    </template>
                    <template v-else>
                      {{ $i18nMachines('reference')?.toLowerCase() }}
                    </template>
                  </div>
                </div>
                <div
                  v-if="Object.keys(materialsMap.materials).length"
                  v-show="
                    !(
                      accordionModels[indexMaterialFamily] &&
                      accordionModels[indexMaterialFamily].includes(indexMaterialClass + 1)
                    )
                  "
                  class="associated-process-families flex flex-row flex-wrap mr-47px mr-5px--xs grid-gap-10px fade-keyframe"
                >
                  <div v-for="processFamilyId in allProcessFamiliesIds" :key="processFamilyId">
                    <vu-icon
                      :class="{
                        'has-processes': Object.values(groupsInClass)
                          .flat()
                          .map((group: any) => group.processFamilyId)
                          .includes(processFamilyId),
                      }"
                      :icon="computeProcessIcon(processFamiliesMap[processFamilyId].name)"
                      v-tooltip.bottom="
                        $i18nProcessF(
                          processFamiliesMap[processFamilyId].name as keyof NlsProcessFamilies,
                        )
                      "
                    />
                  </div>
                </div>
              </div>
            </template>
            <template
              v-for="(groupsInClass, materialClassId, indexMaterialClass) in materialClassList"
              #[`item-${indexMaterialClass+1}`]
              :key="indexMaterialClass + 'material-class-row'"
            >
              <!-- DESKTOP ROW VIEW - with nested material details -->
              <div v-if="!isCardView" class="material-grid pa-10px material-class">
                <div
                  v-for="(materialGroup, indexMaterial) in getVisibleMaterials(
                    materialClassId,
                    groupsInClass,
                  )"
                  :key="indexMaterial + '-index-material-name'"
                  class="display-contents material-group-container"
                >
                  <div v-if="materialsMap.materials[materialGroup[0].id]" class="display-contents">
                    <!-- HEADER LINE OF MATERIAL CLASS -->
                    <template v-if="!indexMaterial">
                      <div class="header-item commercial-name-title pa-10px pl-30px">
                        {{ $i18n('commercialName') }}
                      </div>
                      <div class="header-item manufacturer-title pa-10px">
                        {{ $i18nMachines('manufacturer') }}
                      </div>
                      <div class="header-item model-type pa-10px">
                        {{ $i18nMachines('type') }}
                      </div>
                      <div class="header-item associated-processes-title pa-10px">
                        {{ $i18n('associatedProcesses') }}
                      </div>
                    </template>
                    <!-- Accordion line -->
                    <div
                      v-if="Object.keys(materialsMap.materials).length"
                      class="reference-bloc display-contents"
                    >
                      <div
                        class="accordion-line"
                        @click="
                          toggleReferenceDetails(
                            indexMaterialClass,
                            indexMaterial,
                            materialGroup[0].id,
                          )
                        "
                      >
                        <div class="material-grid-item flex flex-row">
                          <div class="mr-10px">
                            <vu-icon
                              :class="[
                                'reference-icon',
                                {
                                  rotate:
                                    expandedReferences[
                                      getMaterialPositionId(
                                        indexMaterialClass,
                                        indexMaterial,
                                        materialGroup[0].id,
                                      )
                                    ],
                                },
                              ]"
                              icon="expand-right"
                            />
                          </div>
                          <div class="flex flex-row grid-gap-5px">
                            <div class="commercial-name">
                              {{
                                $i18nMaterials(
                                  materialsMap.materials[materialGroup[0].id]
                                    .name as keyof NlsMaterials,
                                )
                              }}
                            </div>
                            <div v-if="materialGroup.length > 1" style="color: #77797c">
                              ({{ materialGroup.length }} {{ $i18n('offers') }})
                            </div>
                          </div>
                        </div>
                        <div class="material-grid-item manufacturer">
                          {{ materialsMap.materials[materialGroup[0].id].manufacturer }}
                        </div>
                        <div class="material-grid-item type">
                          {{
                            $i18nMaterials(
                              materialsMap.materials[materialGroup[0].id].type
                                .name as keyof NlsMaterials,
                              {},
                              true,
                            )
                          }}
                        </div>
                        <div
                          class="fade-keyframe material-grid-item associated-process-families grid-gap-10px flex flex-row flex-wrap"
                        >
                          <div
                            v-for="processFamilyId in allProcessFamiliesIds"
                            :key="processFamilyId"
                          >
                            <vu-icon
                              :data-name="processFamiliesMap[processFamilyId].name"
                              :class="{
                                'has-processes': materialGroup
                                  .map((group: any) => group.processFamilyId)
                                  .includes(processFamilyId),
                              }"
                              :icon="computeProcessIcon(processFamiliesMap[processFamilyId].name)"
                              v-tooltip.bottom="
                                $i18nProcessF(
                                  processFamiliesMap[processFamilyId]
                                    .name as keyof NlsProcessFamilies,
                                )
                              "
                            />
                          </div>
                        </div>
                      </div>
                      <!-- Details for an offer (expanded row mode), we use the created groups by material.id -->
                      <Transition :name="'slide-' + materialGroup.length">
                        <div
                          v-if="
                            expandedReferences[
                              getMaterialPositionId(
                                indexMaterialClass,
                                indexMaterial,
                                materialGroup[0].id,
                              )
                            ]
                          "
                          class="material-group"
                        >
                          <div
                            v-for="(material, materialIndex) in materialGroup"
                            :key="material.id"
                            class="display-contents"
                          >
                            <div class="material-details display-contents">
                              <div class="material-details-item colors-item">
                                <div class="title">
                                  {{ $i18n('colorsLabel') }}
                                </div>
                                <div class="colors flex flex-wrap grid-gap-10px">
                                  <template
                                    v-if="
                                      materialsMap.materials[material.id].colors &&
                                      materialsMap.materials[material.id].colors.length
                                    "
                                  >
                                    <div
                                      v-for="color in materialsMap.materials[material.id].colors"
                                      :key="
                                        'color-' + indexMaterial + '-' + material.id + '-' + color
                                      "
                                      class="color"
                                      :data-color="color"
                                      :style="'background-color: ' + color"
                                    />
                                  </template>
                                  <template v-else>
                                    {{ emptyText }}
                                  </template>
                                </div>
                              </div>
                              <div class="material-details-item finishes">
                                <div class="title">
                                  {{ $i18n('finishLabel') }}
                                </div>
                                <div v-if="material.finishes.length">
                                  <div
                                    v-for="finishName in material.finishes"
                                    :key="indexMaterial + finishName"
                                    class="finish"
                                  >
                                    {{ $i18nProducts(finishName) }}
                                  </div>
                                </div>
                                <div v-else>
                                  {{ emptyText }}
                                </div>
                              </div>
                              <div class="material-details-item">
                                <div class="title">
                                  {{ $i18n('tatLabel') }}
                                </div>
                                <div
                                  :title="getTurnAroundTimeForMaterial(material)"
                                  class="turn-around-time"
                                >
                                  {{ getTurnAroundTimeForMaterial(material) }}
                                </div>
                              </div>
                              <div class="material-details-item">
                                <div class="flex flex-row">
                                  <div class="title">
                                    {{ $i18n('capacityLabel') }}
                                  </div>
                                  <div class="ml-5px mb-10px">
                                    <span
                                      class="fonticon fonticon-info capacity-icon"
                                      @click.stop
                                      v-tooltip.right="
                                        '<b>' +
                                        $i18n('prototypesLabel') +
                                        '</b>: ' +
                                        $i18n('upToPiecesNumber', { number: 20 }) +
                                        '<br>' +
                                        '<b>' +
                                        $i18n('smallSeriesLabel') +
                                        '</b>: ' +
                                        $i18n('upToPiecesNumber', { number: 100 }) +
                                        '<br>' +
                                        '<b>' +
                                        $i18n('seriesLabel') +
                                        '</b>: ' +
                                        $i18n('fromPiecesNumber', { number: 100 })
                                      "
                                    />
                                  </div>
                                </div>
                                <div class="capacity">
                                  <div v-if="material.capacity.includes('P:true')">
                                    {{ $i18n('prototypesLabel') }}
                                  </div>
                                  <div v-if="material.capacity.includes('S1:true')">
                                    {{ $i18n('smallSeriesLabel') }}
                                  </div>
                                  <div v-if="material.capacity.includes('S2:true')">
                                    {{ $i18n('seriesLabel') }}
                                  </div>
                                </div>
                              </div>
                              <div class="material-details-item">
                                <div class="title">
                                  {{ $i18n('toleranceLabel') }}
                                </div>
                                <div>
                                  <div class="tolerance">{{ material.toleranceRate }} %</div>
                                  <div style="font-style: italic">
                                    {{ $i18n('toleranceMin', { minValue: material.toleranceMin }) }}
                                  </div>
                                </div>
                              </div>
                              <div class="material-details-item process">
                                <div class="title">
                                  {{ $i18n('process') }}
                                </div>
                                <div>
                                  <div class="process-family">
                                    <vu-icon
                                      :data-name="processFamiliesMap[material.processFamilyId].name"
                                      class="has-processes"
                                      :icon="
                                        computeProcessIcon(
                                          processFamiliesMap[material.processFamilyId].name,
                                        )
                                      "
                                      v-tooltip.bottom="
                                        $i18nProcessF(
                                          processFamiliesMap[material.processFamilyId]
                                            .name as keyof NlsProcessFamilies,
                                        )
                                      "
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="materialIndex < materialGroup.length - 1"
                              class="border inner"
                            />
                          </div>
                        </div>
                      </Transition>
                    </div>
                    <div class="border outer" />
                  </div>
                </div>
              </div>
              <!-- MOBILE VIEW - All details are directly displayed in a two column grid -->
              <div
                v-else
                :key="indexMaterialClass + 'material-class-row-mobile'"
                class="material-class"
              >
                <div
                  v-for="(materialGroup, indexGroup) in getVisibleMaterials(
                    materialClassId,
                    groupsInClass,
                  )"
                  :key="indexGroup + '-index-material-name'"
                  class="display-contents"
                >
                  <template v-for="(material, indexMaterial) in materialGroup">
                    <div
                      v-if="Object.keys(materialsMap.materials).length"
                      :key="material.id + '/' + indexMaterial"
                      class="mobile-grid fade-keyframe elevated my-10px"
                    >
                      <div class="header-item">
                        {{ $i18n('commercialName') }}
                      </div>
                      <div class="details commercial-name">
                        {{
                          $i18nMaterials(
                            materialsMap.materials[material.id].name as keyof NlsMaterials,
                          )
                        }}
                      </div>
                      <div class="header-item">
                        {{ $i18nMachines('manufacturer') }}
                      </div>
                      <div class="details manufacturer">
                        {{ materialsMap.materials[material.id].manufacturer }}
                      </div>
                      <div class="header-item">
                        {{ $i18nMachines('type') }}
                      </div>
                      <div class="details type">
                        {{
                          $i18nMaterials(
                            materialsMap.materials[material.id].type.name as keyof NlsMaterials,
                          )
                        }}
                      </div>
                      <div class="header-item">
                        {{ $i18n('associatedProcesses') }}
                      </div>
                      <div class="details associated-processes">
                        <div
                          class="fade-keyframe associated-process-families flex flex-row flex-wrap grid-gap-10px"
                        >
                          <div
                            v-for="processFamilyId in allProcessFamiliesIds"
                            :key="processFamilyId"
                          >
                            <vu-icon
                              :data-name="processFamiliesMap[processFamilyId].name"
                              :class="{
                                'has-processes': processFamilyId === material.processFamilyId,
                              }"
                              :icon="computeProcessIcon(processFamiliesMap[processFamilyId].name)"
                              v-tooltip.bottom.touch="
                                $i18nProcessF(
                                  processFamiliesMap[processFamilyId]
                                    .name as keyof NlsProcessFamilies,
                                )
                              "
                            />
                          </div>
                        </div>
                      </div>
                      <div class="header-item">
                        {{ $i18n('colorsLabel') }}
                      </div>
                      <div class="details colors flex flex-wrap grid-gap-10px">
                        <template
                          v-if="
                            materialsMap.materials[material.id].colors &&
                            materialsMap.materials[material.id].colors.length
                          "
                        >
                          <div
                            v-for="color in materialsMap.materials[material.id].colors"
                            :key="'color-' + indexMaterial + '-' + material.id + '-' + color"
                            class="color"
                            :style="'background-color: ' + color"
                          />
                        </template>
                        <template v-else>
                          {{ emptyText }}
                        </template>
                      </div>
                      <div class="header-item">
                        {{ $i18n('finishLabel') }}
                      </div>
                      <div class="details finishes">
                        <div v-if="material.finishes.length">
                          <div
                            v-for="finishName in material.finishes"
                            :key="indexMaterial + finishName"
                            class="finish"
                          >
                            {{ $i18nProducts(finishName) }}
                          </div>
                        </div>
                        <div v-else>
                          {{ emptyText }}
                        </div>
                      </div>
                      <div class="header-item">
                        {{ $i18n('tatLabel') }}
                      </div>
                      <div class="details turn-around-time">
                        <div
                          :title="getTurnAroundTimeForMaterial(material)"
                          class="turn-around-time"
                        >
                          {{ getTurnAroundTimeForMaterial(material) }}
                        </div>
                      </div>
                      <div class="header-item">
                        {{ $i18n('capacityLabel') }}
                      </div>
                      <div class="details capacity">
                        <div class="capacity">
                          <div v-if="material.capacity.includes('P:true')">
                            {{ $i18n('prototypesLabel') }}
                          </div>
                          <div v-if="material.capacity.includes('S1:true')">
                            {{ $i18n('smallSeriesLabel') }}
                          </div>
                          <div v-if="material.capacity.includes('S2:true')">
                            {{ $i18n('seriesLabel') }}
                          </div>
                        </div>
                      </div>
                      <div class="header-item">
                        {{ $i18n('toleranceLabel') }}
                      </div>
                      <div class="details tolerance">
                        <div>
                          <div class="tolerance">{{ material.toleranceRate }} %</div>
                          <div style="font-style: italic">
                            {{ $i18n('toleranceMin', { minValue: material.toleranceMin }) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              <!-- Show more references button -->
              <div
                v-if="getNumReferencesNotDisplayedYet(groupsInClass, materialClassId) > 0"
                :key="`show-more-refs-button-${indexMaterialClass}-${indexMaterialFamily}`"
                class="show-more-refs flex flex-grow-1 mr-10px mb-10px ml-10px"
              >
                <div
                  class="icon-btn ma-10px pt-10px pb-10px pl-50px pr-50px flex flex-row align-self-center"
                  @click="loadMoreReferences(materialClassId)"
                >
                  <div>
                    {{ $i18n('showMoreReferences') }}
                  </div>
                  <div class="ml-5px references-hidden-count">
                    ({{ getNumReferencesNotDisplayedYet(groupsInClass, materialClassId) }})
                  </div>
                </div>
              </div>
            </template>
          </vu-accordion>
        </div>
      </div>
    </template>
    <!-- Case when no materials can be loaded -->
    <div v-else class="no-item pa-10px border-radius-4">
      {{ $i18n('noMaterialsYet') }}
    </div>
  </div>
</template>

<style lang="scss">
// Sliding animation for the vu-accordion
.accordion-container {
  &:not(.active) {
    .accordion-item.active .content-wrapper .content {
      animation: slide-from-top 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
    }
  }

  .accordion .accordion-item .accordion-title .caret-left {
    margin-top: 10px;
  }

  .accordion-title {
    &:hover {
      background: var(--grey-1);
    }
  }

  .reference-icon.vu-icon {
    font-size: 11px;
  }
}
</style>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;
@forward '/assets/scss/skeletons';

// Dynamic transition duration
@for $i from 1 through 30 {
  .slide-#{$i}-enter-active,
  .slide-#{$i}-leave-active {
    transition: all 0.35s;
    overflow: hidden;
    max-height: 280px * $i;
    will-change: max-height;
  }

  .slide-#{$i}-enter,
  .slide-#{$i}-leave-to {
    opacity: 0;
    max-height: 0;
  }
}

.associated-process-families {
  &.material-grid-item {
    line-height: inherit;
  }
}

.chevron {
  transition: transform 0.1s ease-in-out;
  transform: rotateZ(0);

  &.expanded {
    transform: rotateZ(-180deg);
    margin-bottom: 2px;
  }
}

.title-bar {
  min-height: 32px;
}

.content.accordion-animated-content {
  padding-bottom: 0;
  padding-top: 0;
}

.references-count {
  font-size: 16px;
  color: var(--grey-6);

  font-weight: normal;
}

.material-class-title {
  white-space: nowrap;
}

.separator {
  color: var(--grey-5);
  font-size: 14px;
  margin-bottom: 2px;
}

.accordion-item {
  &:not(:first-child) {
    border-top: 1px solid var(--grey-4);
  }

  .material-class-name {
    line-height: 12px;
    font-size: 16px;
    font-weight: bold;
    color: var(--grey-6);
  }
}

.show-more-refs {
  background: #f8f8f8;
  justify-content: center;
  cursor: pointer;

  .icon-btn {
    border: 1px solid var(--grey-5);
    border-radius: 5px;
    font-size: 14px;
    background-color: var(--grey-2);
    cursor: pointer;
    user-select: none;

    .vu-icon {
      color: black;
    }

    &:hover {
      background-color: var(--grey-1);
    }
  }
}

.colors {
  row-gap: 5px;

  .color {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    border: 1px solid #b4b6ba;
  }
}

.material-group {
  display: grid;
  grid-column: 1 / 5;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-flow: row;
  background: #f9f9f9;
}

.mobile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid var(--grey-4);

  .header-item {
    padding: 10px;
    font-weight: bold;
    background: #ebebeb;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .details {
    padding: 10px;

    &.commercial-name {
      color: rgb(54, 142, 196);
    }
  }
}

.material-details {
  grid-column: 1/5;
  background-color: #f7f7f7;

  .material-details-item {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin: 10px;
    padding: 10px;

    &.colors-item {
      padding-left: 30px;
    }

    .title {
      font-weight: bold;
      margin-bottom: 10px;
    }

    .vu-icon {
      color: var(--grey-6);

      &.has-processes {
        font-size: 18px;
        color: var(--blue-3);

        &:hover {
          color: var(--blue-2);
        }
      }
    }

    &:not(:first-child) {
      border-left: solid 1px #e5e5e5;
    }

    &.no-border {
      border: none;
    }

    > .colors {
      max-width: 120px;
    }

    &.process {
      padding-left: 25px;
    }
  }
}

.vu-icon {
  font-size: 20px;
  color: var(--grey-3);

  &.has-processes {
    color: var(--blue-3);

    &:hover {
      color: var(--blue-2);
    }
  }

  @include variables.for-xs {
    font-size: initial;
    gap: 10px;
  }
}

.references-hidden-count {
  color: var(--grey-6);
}

.reference-icon {
  color: var(--grey-5);
  transition: transform 0.2s ease-in-out;

  &.rotate {
    transform: rotate(90deg);
    color: var(--grey-6);
  }
}

.header-item {
  font-weight: bold;
  background: #ebebeb;
  height: 100%;
  display: flex;
  align-items: center;
}

.border {
  grid-column: 1 / -1;
  height: 1px;
  border-bottom: 1px solid var(--grey-4);
  width: 100%;
}

.material-grid .material-group-container:last-child .border.outer {
  display: none;
}

.material-grid-item {
  line-height: 14px;
  font-size: 14px;
  padding: 10px;
  cursor: pointer;

  &:hover {
    background-color: var(--grey-1);
  }
}

.material-grid {
  display: grid;
  align-items: center;
  grid-template-columns: 3fr 3fr 2fr 2fr;
  overflow-y: hidden;

  .accordion-line {
    display: contents;
  }
}

.no-item {
  background: white;
  border: var(--grey-4) solid 1px;
}

.accordion-item:not(:first-child) {
  border-top: 1px solid #d1d4d4;
}
</style>
